import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { configureShopifyStore, isShopifyStoreConfigured } from '@/lib/shopify';

/**
 * Hook to initialize the Shopify Storefront API config from the merchant's DB record.
 * Call this in any page/component that needs to use the Storefront API.
 * 
 * @param merchantId - Optional merchant ID. If not provided, fetches the first available merchant.
 */
export function useShopifyStoreConfig(merchantId?: string | null) {
  const [loading, setLoading] = useState(true);
  const [configured, setConfigured] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // If already configured (from a previous call), skip
    if (isShopifyStoreConfigured()) {
      setConfigured(true);
      setLoading(false);
      return;
    }

    const loadConfig = async () => {
      try {
        let query = supabase
          .from('merchants')
          .select('shopify_store_domain, storefront_access_token')
          .not('storefront_access_token', 'is', null)
          .not('shopify_store_domain', 'is', null);

        if (merchantId) {
          query = query.eq('id', merchantId);
        }

        const { data, error: dbError } = await query.limit(1).maybeSingle();

        if (dbError) {
          console.error('Error loading Shopify store config:', dbError);
          setError('Failed to load store configuration');
          return;
        }

        if (data?.shopify_store_domain && data?.storefront_access_token) {
          configureShopifyStore(data.shopify_store_domain, data.storefront_access_token);
          setConfigured(true);
        } else {
          setError('No store with storefront access token found. Please configure it in merchant settings.');
        }
      } catch (err) {
        console.error('Error initializing Shopify store:', err);
        setError('Failed to initialize store');
      } finally {
        setLoading(false);
      }
    };

    loadConfig();
  }, [merchantId]);

  return { loading, configured, error };
}
